import { useEventOccurrencesQuery } from "@ticketingplatform/api/dist/event";
import { Skeleton } from "antd";
import { useVenueContext } from "contexts/VenueContext";
import addYears from "date-fns/addYears";
import getMonth from "date-fns/getMonth";
import getYear from "date-fns/getYear";
import subYears from "date-fns/subYears";
import { useCallback, useMemo, useState } from "react";
import getEndOfMonthUTC from "utils/getEndOfMonthUTC";
import getStartOfMonthUTC from "utils/getStartOfMonthUTC";
import parseDate from "utils/parseDate";

import CalendarEventItem from "./partials/CalendarEventItem";
import { UseCalendar } from "./types";

export const useCalendar: UseCalendar = () => {
  const { venue } = useVenueContext();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const { data, loading } = useEventOccurrencesQuery({
    skip: !venue,
    variables: {
      filters: venue
        ? {
            venueId: { equals: venue.id },
          }
        : {},
      start: getStartOfMonthUTC(getMonth(selectedDate), getYear(selectedDate)),
      end: getEndOfMonthUTC(getMonth(selectedDate), getYear(selectedDate)),
      eventInclude: true,
    },
  });

  const dateCellRender = useCallback(
    (value: Date) => {
      if (loading) {
        return <Skeleton paragraph={{ rows: 1, width: "100%" }} />;
      }

      const listData = data?.eventOccurrences?.nodes.filter(({ date }) => {
        const eventDate = parseDate(date);

        return (
          eventDate?.getDate() === value.getDate() &&
          eventDate?.getMonth() === value.getMonth() &&
          eventDate?.getFullYear() === value.getFullYear()
        );
      });

      return listData ? (
        <ul className="events">
          {listData.map(({ id, event }) => {
            // `event` is behind an @include directive so codegen types it as
            // optional; eventInclude is always true here, so it is present.
            const { name, status, description, id: eventId } = event!;

            return (
              <li key={id}>
                <CalendarEventItem
                  {...{ name, status, eventId }}
                  description={description || ""}
                  occurrenceId={id}
                />
              </li>
            );
          })}
        </ul>
      ) : null;
    },
    [data?.eventOccurrences?.nodes, loading]
  );

  return {
    mode: "month",
    value: selectedDate,
    onChange: setSelectedDate,
    validRange: useMemo(
      () => [subYears(new Date(), 10), addYears(new Date(), 3)],
      []
    ),
    dateCellRender,
  };
};
